// Mirrors internal/kinds/agentrun Apply: factory sessions only, and every action
// only queues a write-back; the factory takes it and the next mirror shows it.
import type { Resource } from '@/db/types'
import { compact, conflict, invalid } from '@/kinds/kind'
import { formatTime } from '@/lib/time'
import type { WriteBack } from '@/kinds/git/schema'
import { isOver, type AgentRunSpec, type AgentRunStatus } from './schema'

export type AgentRunResource = Resource<AgentRunSpec, AgentRunStatus>

const asObj = (a: unknown): Record<string, unknown> =>
  a && typeof a === 'object' ? (a as Record<string, unknown>) : {}

export const defaults = (spec: Partial<AgentRunSpec>): AgentRunSpec =>
  ({
    project_id: '',
    title: '',
    repo_url: '',
    pr_url: '',
    workspace: '',
    started_at: '',
    updated_at: '',
    events: [],
    ...spec,
  }) as AgentRunSpec

export const initialStatus = (r: AgentRunResource): AgentRunStatus => ({
  last_touched: formatTime(new Date(r.created_at)),
})

export const displayName = (r: AgentRunResource) =>
  r.spec.title ||
  r.spec.report?.headline ||
  (r.spec.name ? `agent ${r.spec.name}` : `run ${r.spec.issue_id ?? ''}`)

const TASK_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/** How many pages sit beside the lead's thread; the oldest goes (Go: MaxArtifacts). */
export const MAX_ARTIFACTS = 8

type Artifact = NonNullable<AgentRunStatus['artifacts']>[number]

/** show_artifact, close_artifact, artifact_seen (Go: applyArtifact); null for any other action. */
const applyArtifact = (
  r: AgentRunResource,
  action: string,
  a: Record<string, unknown>,
  at: string,
): AgentRunResource | null => {
  if (action !== 'show_artifact' && action !== 'close_artifact' && action !== 'artifact_seen') return null
  const id = a.page_id
  if (typeof id !== 'string' || !TASK_ID.test(id)) throw invalid(`${action}: page_id must be a page id`)
  const seen = typeof a.seen === 'string' ? a.seen : ''
  const list = r.status.artifacts ?? []
  const i = list.findIndex((x) => x.id === id)
  let next: Artifact[] = list
  if (action === 'show_artifact') {
    const cur: Artifact = {
      id,
      at: formatTime(new Date(at)),
      ...(seen || list[i]?.seen ? { seen: seen || list[i]?.seen } : {}),
    }
    next = [cur, ...list.filter((_, j) => j !== i)].slice(0, MAX_ARTIFACTS)
  } else if (action === 'close_artifact') {
    if (i >= 0) next = list.filter((_, j) => j !== i)
  } else {
    if (!seen) throw invalid(`${action}: seen is required`)
    if (i >= 0) next = list.map((x, j) => (j === i ? { ...x, seen } : x))
  }
  return {
    ...r,
    status: compact({ ...r.status, artifacts: next, last_touched: formatTime(new Date(at)) }),
  }
}

export const apply = (r: AgentRunResource, action: string, args: unknown, at: string): AgentRunResource => {
  if (r.spec.source.provider !== 'factory') throw invalid('only factory sessions take actions')
  const a = asObj(args)
  if (action === 'link_task') {
    // local: ties the session to its task for the board (Go: ActionLinkTask)
    const id = a.task_id
    if (typeof id !== 'string' || (id !== '' && !TASK_ID.test(id)))
      throw invalid('link_task: task_id must be a task id')
    return { ...r, status: compact({ ...r.status, task_id: id, last_touched: formatTime(new Date(at)) }) }
  }
  const shown = applyArtifact(r, action, a, at)
  if (shown) return shown
  if (isOver(r.spec.status)) throw conflict('the session has ended')
  let queued: Record<string, unknown> | undefined
  switch (action) {
    case 'say':
      if (typeof a.text !== 'string' || !a.text) throw invalid('say: text is required')
      queued = a
      break
    case 'interrupt':
      if (r.spec.status !== 'Working') throw conflict('the agent is not working on anything')
      break
    case 'end':
      break
    default:
      throw invalid(`unknown action ${action}`)
  }
  const st: AgentRunStatus = { ...r.status }
  if (action !== 'say' && (st.writebacks ?? []).some((wb) => wb.action === action))
    throw conflict('already asked')
  const when = formatTime(new Date(at))
  const wb: WriteBack = { action, at: when }
  if (queued && Object.keys(queued).length) wb.args = queued
  st.writebacks = [...(st.writebacks ?? []), wb]
  st.writeback_error = undefined
  st.last_touched = when
  return { ...r, status: compact(st) }
}
